/**
 * ProTable 数据请求 Hook
 *
 */

import { useCallback, useRef, useState } from 'react';
import type {
  ApiResponse,
  PaginatedData,
  ProTableResponse,
} from '../types/api';
import { normalizeError } from './shared-async';
import { fetchPaginatedData, type TableRequestParams } from './shared-request';
import { useLatestRef } from './useLatestRef';

/**
 * useTableData 配置
 */
export interface UseTableDataOptions<T, P = Record<string, unknown>> {
  /** API 请求函数 */
  fetchApi: (params: P) => Promise<ApiResponse<PaginatedData<T>>>;
  /** 成功状态码，默认 '200' */
  successCode?: string | number;
  /** 请求前的参数转换 */
  transformParams?: (params: TableRequestParams) => P;
  /** 响应后的数据转换 */
  transformData?: (list: T[]) => T[];
  /** 请求失败回调 */
  onError?: (error: Error) => void;
  /** 数据加载完成回调 */
  onDataLoaded?: (data: T[], total: number) => void;
  /** 默认额外查询参数 */
  defaultParams?: Partial<P>;
}

/**
 * useTableData 返回值
 */
export interface UseTableDataReturn<T, P = Record<string, unknown>> {
  /** 传递给 ProTable 的 request 函数 */
  request: (
    params: TableRequestParams,
    sort?: Record<string, 'ascend' | 'descend' | null>,
    filter?: TableRequestParams,
  ) => Promise<ProTableResponse<T>>;
  /** 刷新表格 (需要配合 actionRef) */
  reload: (resetPageIndex?: boolean) => Promise<void>;
  /** 刷新并重置分页到第一页 */
  reloadAndRest: () => Promise<void>;
  /** 设置额外参数并重新加载（重置分页） */
  search: (params?: Partial<P>) => void;
  /** 重置额外参数并重新加载 */
  reset: () => void;
  /** 设置额外查询参数（不触发请求） */
  setExtraParams: (params: Partial<P>) => void;
  /** 当前额外查询参数 */
  extraParams: Partial<P>;
  /** 加载中状态 */
  loading: boolean;
  /** actionRef - 传给 ProTable */
  actionRef: React.MutableRefObject<TableActionRef | undefined>;
}

interface TablePageInfo {
  pageSize: number;
  total: number;
  current: number;
}

type TableEditableKey = React.Key | React.Key[];

type TableEditableRecord = Record<string, unknown>;

interface TableEditableState {
  recordKey: string;
  isEditable: unknown;
  preIsEditable: unknown;
}

interface TableActionRef {
  reload: (resetPageIndex?: boolean) => Promise<void>;
  reloadAndRest?: () => Promise<void>;
  reset?: () => void;
  clearSelected?: () => void;
  pageInfo?: TablePageInfo;
  isEditable: (
    row: TableEditableRecord & {
      index: number;
    },
  ) => TableEditableState;
  startEditable: (
    recordKey: React.Key,
    record?: TableEditableRecord,
  ) => boolean;
  cancelEditable: (
    recordKey: TableEditableKey,
    needReTry?: boolean,
  ) => Promise<true | undefined>;
  addEditRecord: (row: TableEditableRecord, options?: unknown) => boolean;
  saveEditable: (
    recordKey: TableEditableKey,
    needReTry?: boolean,
  ) => Promise<boolean>;
  preEditableKeys: React.Key[] | undefined;
  onValuesChange: (
    value: TableEditableRecord,
    values: TableEditableRecord,
  ) => void;
  getRealIndex: ((record: TableEditableRecord) => number) | undefined;
}

/**
 * ProTable 数据请求 Hook
 *
 * @example
 * ```tsx
 * const { request, actionRef, reload, loading } = useTableData({
 *   fetchApi: (params) => getUserList(params),
 *   transformParams: (params) => ({
 *     pageNo: params.current,
 *     pageSize: params.pageSize,
 *     ...params,
 *   }),
 *   onDataLoaded: (data, total) => console.log('loaded', total),
 * });
 *
 * <ProTable actionRef={actionRef} request={request} columns={columns} />
 * ```
 */
export function useTableData<T, P = Record<string, unknown>>(
  options: UseTableDataOptions<T, P>,
): UseTableDataReturn<T, P> {
  const { fetchApi, successCode = '200' } = options;

  const actionRef = useRef<TableActionRef | undefined>(undefined);
  const defaultParamsRef = useLatestRef(options.defaultParams ?? {});
  const [extraParams, setExtraParamsState] = useState<Partial<P>>(
    (options.defaultParams ?? {}) as Partial<P>,
  );
  const [loading, setLoading] = useState(false);

  // 用 useLatestRef 稳定回调引用，防止内联函数导致 ProTable 不必要的重渲染
  const transformParamsRef = useLatestRef(options.transformParams);
  const transformDataRef = useLatestRef(options.transformData);
  const onErrorRef = useLatestRef(options.onError);
  const onDataLoadedRef = useLatestRef(options.onDataLoaded);

  const request = useCallback(
    async (
      params: TableRequestParams,
      sort?: Record<string, 'ascend' | 'descend' | null>,
      filter?: TableRequestParams,
    ): Promise<ProTableResponse<T>> => {
      setLoading(true);
      try {
        const mergedParams = { ...params, ...extraParams };
        const result = await fetchPaginatedData<T, P>({
          params: mergedParams,
          sort,
          filter,
          fetchApi,
          successCode,
          transformParams: transformParamsRef.current,
          transformData: transformDataRef.current,
        });

        if (result.success) {
          onDataLoadedRef.current?.(result.data, result.total);
        }

        return result;
      } catch (error) {
        onErrorRef.current?.(normalizeError(error));
        return { data: [], success: false, total: 0 };
      } finally {
        setLoading(false);
      }
    },
    [
      fetchApi,
      successCode,
      extraParams,
      onDataLoadedRef,
      onErrorRef,
      transformDataRef,
      transformParamsRef,
    ],
  );

  const reload = useCallback(async (resetPageIndex?: boolean) => {
    await actionRef.current?.reload(resetPageIndex);
  }, []);

  const reloadAndRest = useCallback(async () => {
    await actionRef.current?.reloadAndRest?.();
  }, []);

  const search = useCallback((newParams?: Partial<P>) => {
    if (newParams) {
      setExtraParamsState((prev) => ({ ...prev, ...newParams }));
    }
    queueMicrotask(() => actionRef.current?.reloadAndRest?.());
  }, []);

  const reset = useCallback(() => {
    const defaults = defaultParamsRef.current as Partial<P>;
    setExtraParamsState(defaults);
    queueMicrotask(() => actionRef.current?.reloadAndRest?.());
  }, [defaultParamsRef]);

  const setExtraParams = useCallback((params: Partial<P>) => {
    setExtraParamsState((prev) => ({ ...prev, ...params }));
  }, []);

  return {
    request,
    reload,
    reloadAndRest,
    search,
    reset,
    setExtraParams,
    extraParams,
    loading,
    actionRef,
  };
}
